/**
 * Track Mobile — the pure parts of the phone screens.
 *
 * No DOM, no Firestore, no state: each function takes plain values and returns
 * plain values, so the rules a screen draws by are tested in node
 * (test/phone-model.test.mjs) rather than found wrong on a site.
 */

/**
 * Who a mail thread is from, as a person would say it. The Gmail layer hands
 * `from` over as an object ({ name, email }, google-api.js parseFrom); older
 * rows and the archiver's index carry a header string ("Ben Ward <ben@…>").
 * Interpolating the object gave every sender "[object Object]" (27/10/2026).
 * The desktop reader's rule (views/mail-thread.js): the name, else the address.
 */
export function senderName(from) {
  if (!from) return '';
  if (typeof from === 'object') return String(from.name || from.email || '').trim();
  const s = String(from).trim();
  const m = s.match(/^"?([^"<]+?)"?\s*<([^>]+)>/);
  if (m) return m[1].trim() || m[2].trim();
  return s.replace(/^<|>$/g, '');
}

/** The address part of a sender, whichever shape it came in. */
export function senderEmail(from) {
  if (!from) return '';
  if (typeof from === 'object') return String(from.email || '').trim().toLowerCase();
  const s = String(from);
  const m = s.match(/<([^>]+)>/) || s.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
  return m ? String(m[1] || m[0]).trim().toLowerCase() : '';
}

/** A phone number a `tel:` link can dial: digits and a leading +, or '' when there isn't one. */
export function telHref(number) {
  const raw = String(number == null ? '' : number).trim();
  if (!raw || raw === '0') return '';
  const n = raw.replace(/(?!^\+)[^\d]/g, '');
  return n.replace(/\D/g, '').length >= 6 ? `tel:${n}` : '';
}

/** `mailto:` / `sms:` for a real address or number, else ''. */
export function mailHref(email, subject = '') {
  const e = String(email || '').trim();
  if (!/^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/.test(e)) return '';
  return `mailto:${encodeURIComponent(e).replace(/%40/g, '@')}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`;
}
export function smsHref(number) {
  const t = telHref(number);
  return t ? `sms:${t.slice(4)}` : '';
}

/**
 * The one gate every link the phone draws goes through: http(s) as the
 * desktop's safeUrl allows, plus the three a phone needs — tel:, sms: (digits
 * only) and mailto: (no spaces, quotes or angle brackets). Anything else is ''
 * and the caller draws no link at all.
 */
export function safeLink(u) {
  const s = String(u == null ? '' : u).trim();
  if (/^https?:\/\/[^\s"'<>]+$/i.test(s)) return s;
  if (/^(tel|sms):\+?\d{6,}$/.test(s)) return s;
  if (/^mailto:[^\s"'<>]+$/i.test(s)) return s;
  return '';
}

/* ── v4.14.0 — the redesign's rules ──────────────────────────────────────── */

const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;
const VERB = /^(check|review|sign|issue|chase|reply|answer|approve|send|call|book|visit|finish|draft|price|update|confirm|ask|fix|mark|log|file)\b/i;

/**
 * One Needs-you row, verb first (Ph2Today): the thing to do as the title, the
 * project and how long under it, and ONE button — the kind tag is gone because
 * the verb already says it. `r` is a loadHome row.
 */
export function needsLine(r) {
  const ref = r.ref ? `${r.ref} · ` : '';
  switch (r.kind) {
    case 'late':
      return { title: `Get ${r.title} back on track`, sub: `${ref}${r.meta}`, verb: 'Open' };
    case 'sign':
      return { title: `Sign off ${r.title}`, sub: `${ref}${String(r.meta || '').replace(/^Ready to issue · ?/, '') || 'ready to issue'}`, verb: r.verb };
    case 'check': {
      const t = String(r.title || 'the work');
      return { title: VERB.test(t) ? t : `Check ${t.charAt(0).toLowerCase()}${t.slice(1)}`, sub: `${ref}${String(r.meta || '').replace(/^From /, 'from ')}`, verb: 'Check' };
    }
    case 'money':
      return { title: r.amount ? `Chase ${r.amount} overdue` : `Chase ${r.title}`, sub: `${r.ref ? `Inv ${r.ref} · ` : ''}${r.amount ? `${r.title} · ` : ''}${r.meta}`, verb: 'Chase' };
    case 'review':
      return { title: `Review the ${String(r.title || '').replace(/^Fee proposal — /, 'fee proposal for ')}`, sub: `${ref}${r.meta}`, verb: 'Review' };
    default:
      return { title: r.title || '', sub: `${ref}${r.meta || ''}`, verb: r.verb || 'Open' };
  }
}

/**
 * Which instructions can take time (D4: time sits on an instruction). Live =
 * not Complete, Void, Cancelled or a Draft — the desktop Log time's rule plus
 * drafts, which are not live work yet. Drafts come back separately so the
 * sheet can show them, disabled ("I2 · Draft, not yet live").
 */
const CLOSED = ['Complete', 'Void', 'Cancelled'];
export function loggableInstructions(list = []) {
  const sorted = [...(list || [])].sort((a, b) => Number(a.seq) - Number(b.seq));
  const isDraft = (i) => i.draft === true || String(i.status || '').toLowerCase() === 'draft';
  return {
    live: sorted.filter((i) => !CLOSED.includes(i.status) && !isDraft(i)),
    drafts: sorted.filter((i) => isDraft(i)),
  };
}

/** Great-circle distance in km (haversine). */
export function km(a, b) {
  const R = 6371; const r = (x) => (x * Math.PI) / 180;
  const dLat = r(b.lat - a.lat); const dLng = r(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Projects for the Site visit picker, nearest first from where the phone is.
 * A project with a pinned site (siteLat/siteLng) gets its distance; one without
 * keeps its place after them. No location → the order given (most recent first).
 */
export function nearestFirst(projects = [], here = null) {
  const withD = projects.map((p, i) => {
    const ok = here && p.siteLat != null && p.siteLng != null && Number.isFinite(Number(p.siteLat)) && Number.isFinite(Number(p.siteLng));
    return { p, i, d: ok ? km(here, { lat: Number(p.siteLat), lng: Number(p.siteLng) }) : null };
  });
  withD.sort((a, b) => (a.d == null) - (b.d == null) || (a.d != null && b.d != null ? a.d - b.d : a.i - b.i));
  return withD.map(({ p, d }) => ({ ...p, distanceKm: d == null ? null : Math.round(d * 10) / 10 }));
}
export const distanceText = (dkm) => (dkm == null ? '' : dkm < 1 ? 'here' : `${Math.round(dkm * 0.621371 * 10) / 10} mi`);

const pad = (n) => String(n).padStart(2, '0');
export const clock = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const hmText = (mins) => `${Math.floor(mins / 60)}:${pad(Math.round(mins % 60))}`;
/** Round to the nearest quarter hour, never under 0:15. */
export const quarter = (mins) => Math.max(15, Math.round((Number(mins) || 0) / 15) * 15);

/**
 * The + Add sheet's one suggestion (Ph2Add): today's site visit (or check-in)
 * on a project that has no time logged today yet → "Log 1:30 on 8601?".
 * `visits` [{ projectId, number, start, end, address }], `loggedNumbers` the
 * project numbers with time today. Newest finished visit wins; null when Track
 * has nothing good to say.
 */
export function addSuggestion({ visits = [], loggedNumbers = [], now = new Date() } = {}) {
  const today = (d) => d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  const logged = new Set((loggedNumbers || []).map(String));
  const cands = (visits || []).map((v) => ({ ...v, s: new Date(v.start), e: v.end ? new Date(v.end) : null }))
    .filter((v) => v.e && !Number.isNaN(v.s.getTime()) && !Number.isNaN(v.e.getTime()) && v.e > v.s && today(v.s) && v.e <= now && !logged.has(String(v.number)))
    .sort((a, b) => b.e - a.e);
  const v = cands[0];
  if (!v) return null;
  const mins = quarter((v.e - v.s) / 60000);
  return {
    projectId: v.projectId || null, number: String(v.number), mins,
    from: clock(v.s), to: clock(v.e),
    title: `Log ${hmText(mins)} on ${v.number}?`,
    sub: `You were on site${v.address ? ` at ${v.address}` : ''}, ${clock(v.s)}–${clock(v.e)}`,
  };
}

/**
 * Mail's suggested project (Ph2Mail). The matcher is the desktop's — mailTriage
 * (functions/mail-triage.js, the Gmail panel's gmail-match) — so its answer is
 * used as given: matched/suggested → that project; filed → filed. Without an
 * answer yet (no signal, still asking) only what the desktop's own fallback
 * trusts: a TRACK// tag in the subject. Never a second matcher.
 */
export function mailSuggestion(thread = {}, triage = null, tagOf = () => null) {
  if (triage && triage.state === 'filed') return { state: 'filed', job: triage.job || null, reason: 'Filed' };
  if (triage && (triage.state === 'matched' || triage.state === 'suggested') && triage.job) {
    return { state: triage.state, job: String(triage.job), reason: triage.reason || '' };
  }
  const tag = tagOf(thread.subject || '');
  if (tag && !/^A/i.test(tag)) return { state: 'matched', job: String(tag), reason: 'Project number in subject' };
  return { state: triage ? 'none' : 'pending', job: null, reason: (triage && triage.reason) || '' };
}

/**
 * Today's Next card (Ph2Today): the event under way or the next one today.
 * `events` [{ start: Date, end?: Date, kind: 'visit'|'meeting'|'task', … }].
 * An event that started up to its end (or 30 min) ago still counts as next.
 */
export function nextEvent(events = [], now = new Date()) {
  const live = (events || []).filter((e) => e && e.start instanceof Date && !Number.isNaN(e.start.getTime()))
    .filter((e) => (e.end instanceof Date ? e.end : new Date(e.start.getTime() + 30 * 60000)) > now)
    .sort((a, b) => a.start - b.start);
  return live[0] || null;
}
/** "in 25 min", "in 2h 10m", "now". */
export function untilText(start, now = new Date()) {
  const m = Math.round((start - now) / 60000);
  if (m <= 0) return 'now';
  if (m < 60) return `in ${m} min`;
  return `in ${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}`;
}

/** "3 days" / "today" for an age in days. */
export const ageText = (n) => (n ? plural(n, 'day') : 'today');

/**
 * MORE FOR A CONTRACTOR (v4.14.8) — PURE. The cells of the contractor phone's
 * More grid, by role: [groupLabel, [[id, label, act, icon, meta|null], …]].
 * Time is here for the Owner (their tab is Approve); Approve for the Office.
 */
const OFFICE = ['Owner', 'Office'];
export function contractorMoreCells(roles = [], d = {}, { quotes = false, approveIsTab = roles.includes('Owner') } = {}) {
  const office = roles.some((r) => OFFICE.includes(r));
  const hm = (m) => `${Math.floor(m / 60)}:${String(Math.round(m % 60)).padStart(2, '0')}`;
  const timeText = d.checkedIn ? 'Checked in' : d.todayMins != null ? `${hm(d.todayMins)} today` : '';
  return [
    ['Your work', [
      ...(approveIsTab ? [['time', 'Time', 'go:time', 'time', timeText ? { text: timeText } : null]] : []),
      ['spend', 'Expenses', 'go:spend', 'invoices', d.spendWaiting != null ? { text: d.spendWaiting ? `${d.spendWaiting} waiting` : 'Receipts and mileage' } : { text: 'Receipts and mileage' }],
      ...(office && !approveIsTab ? [['approve', 'Approve', 'go:approve', 'tasks', d.approveWaiting != null ? { text: d.approveWaiting ? `${d.approveWaiting} waiting` : 'Nothing waiting', warn: d.approveWaiting > 0 } : null]] : []),
    ]],
    ['Your company', [
      ...(office && quotes ? [['measure', 'Measure', 'tv-measure', 'calculate', { text: 'For a quote' }]] : []),
      ...(roles.includes('Owner') ? [['invite', 'Add someone', 'tv-invite', 'team', { text: 'A new starter on site' }]] : []),
      ['help', 'Help', 'tv-help', 'help', { text: 'Walkthroughs' }],
    ]],
  ].filter(([, cells]) => cells.length);
}

/**
 * v4.14.9 — the home-screen icon from an older Track (WebKit 301108). An icon
 * added while Track asked for a see-through status bar keeps that setting, and
 * on some iPhones the page then gets a window that starts under the status bar
 * but stops short of the screen's bottom by the status bar's height (Karan's:
 * screen 956, window 894, safe area top 62). Nothing the page does can paint
 * below that window; re-adding the icon is the fix. True only for exactly that
 * case: standalone, the window starts at the top of the screen, the top safe
 * area is a real status bar (over 30), and the shortfall equals it (±4).
 */
export function oldIconShortWindow({ standalone = false, screenH = 0, innerH = 0, safeTop = 0, windowTop = 0 } = {}) {
  if (!standalone) return false;
  const gap = Number(screenH) - Number(innerH);
  const top = Number(safeTop) || 0;
  if (!(top > 30) || !(gap > 0)) return false;
  if (Number(windowTop) !== 0) return false;
  return Math.abs(gap - top) <= 4;
}
